'use client';

import Link from 'next/link';
import Image from 'next/image';
import Cookies from 'js-cookie';
import classes from './NeumoHeader.module.scss';
import { useEffect, useMemo, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';

// ✅ Mantine
import { Menu, UnstyledButton, Group } from '@mantine/core';

type HeaderLink = { label: string; href: string; description?: string };

type HeaderColumn = {
  title: string;
  links: HeaderLink[];
};

type HeaderNavItem = {
  label: string;
  href?: string;
  columns?: HeaderColumn[];
};

export type NeumoHeaderSlice = {
  type: 'neumo-header';
  logoSrc: string;
  logoAlt: string;
  nav: HeaderNavItem[];
  cta?: { label: string; href: string };

  auth: {
    dashboardLabel: string;
    dashboardHref: string;
    loginLabel: string;
    loginHref: string;
    logoutLabel: string;
    afterLogoutHref?: string;
  };

  supportedLanguage: {
    fr: { label: string; img: string };
    en: { label: string; img: string };
  };
  selectedLanguage?: 'fr' | 'en';
};

// --- helpers ---
const LOCALES = ['fr', 'en'] as const;
type Locale = (typeof LOCALES)[number];

function getLocaleFromPath(pathname: string): Locale | null {
  const first = pathname.split('/')[1];
  return (LOCALES as readonly string[]).includes(first) ? (first as Locale) : null;
}

function withLocalePrefix(pathname: string, nextLocale: Locale): string {
  const parts = pathname.split('/');
  const first = parts[1];

  if ((LOCALES as readonly string[]).includes(first)) {
    parts[1] = nextLocale; // swap /fr/... -> /en/... etc.
    return parts.join('/') || `/${nextLocale}`;
  }

  // if no prefix, add it
  return `/${nextLocale}${pathname.startsWith('/') ? '' : '/'}${pathname}`;
}

export function NeumoHeader({ data }: { data: NeumoHeaderSlice }) {
  const pathname = usePathname();
  const router = useRouter();

  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Re-check cookie on navigation (enough for your case)
  useEffect(() => {
    setIsLoggedIn(!!Cookies.get('authToken'));
  }, [pathname]);

  const handleLogout = () => {
    Cookies.remove('authToken');
    window.location.href = data.auth.afterLogoutHref ?? '/';
  };

  // ✅ current locale (priority: cookie -> path -> data.selectedLanguage -> 'fr')
  const currentLocale: Locale = useMemo(() => {
    const cookieLocale = Cookies.get('NEXT_LOCALE') as Locale | undefined;
    if (cookieLocale === 'fr' || cookieLocale === 'en') return cookieLocale;

    const pathLocale = getLocaleFromPath(pathname);
    if (pathLocale) return pathLocale;

    if (data.selectedLanguage === 'fr' || data.selectedLanguage === 'en') return data.selectedLanguage;

    return 'fr';
  }, [pathname, data.selectedLanguage]);

  const changeLanguage = (nextLocale: Locale) => {
    if (nextLocale === currentLocale) return;

    // ✅ set Next.js locale cookie
    Cookies.set('NEXT_LOCALE', nextLocale, { expires: 365, path: '/' });

    // ✅ keep same page, just swap locale prefix
    const nextPath = withLocalePrefix(pathname, nextLocale);
    router.push(nextPath);
    router.refresh(); // helps ensure dictionary fetch picks new locale
  };

  const currentLangMeta = data.supportedLanguage[currentLocale];

  return (
    <header className={classes.header} role="banner">
      <div className={classes.inner}>
        <Link href="/" className={classes.logo} aria-label="Home">
          <Image
            src={data.logoSrc}
            alt={data.logoAlt}
            width={34}
            height={34}
            priority
            sizes="34px"
            quality={75}
            className={classes.logoImg}
          />
          <span className={classes.logoText}>Hestia Innovation</span>
        </Link>

        <nav className={classes.nav} aria-label="Primary">
          <ul className={classes.navList}>
            {data.nav.map((item) => {
              const hasDropdown = item.columns && item.columns.length > 0;

              if (!hasDropdown) {
                return (
                  <li key={item.label} className={classes.navItem}>
                    <Link className={classes.navLink} href={item.href ?? '#'}>
                      {item.label}
                    </Link>
                  </li>
                );
              }

              return (
                <li key={item.label} className={`${classes.navItem} ${classes.hasDropdown}`}>
                  <button type="button" className={classes.navButton} aria-haspopup="true">
                    {item.label}
                    <span className={classes.chevron} aria-hidden="true">
                      ▾
                    </span>
                  </button>

                  <div className={classes.mega} role="menu">
                    <div className={classes.megaInner}>
                      {item.columns!.map((col) => (
                        <div key={col.title} className={classes.col}>
                          <p className={classes.colTitle}>{col.title}</p>
                          <ul className={classes.colList}>
                            {col.links.map((l) => (
                              <li key={l.href} className={classes.colItem}>
                                <Link href={l.href} className={classes.megaLink}>
                                  <span className={classes.megaLabel}>{l.label}</span>
                                  {l.description && <span className={classes.megaDesc}>{l.description}</span>}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className={classes.actions}>
          {/* ✅ right side: language + auth */}
          <div className={classes.rightLinks}>
            {/* ✅ Language selector */}
            <Menu withinPortal position="bottom-end" offset={8} shadow="md">
              <Menu.Target>
                <UnstyledButton className={classes.langButton} aria-label="Change language">
                  <Group gap={8} wrap="nowrap">
                    {/* use normal img to avoid Next Image remote config friction */}
                    {/* if you already configured domains, you can use <Image> */}
                    <Image
                      src={currentLangMeta.img}
                      alt={currentLangMeta.label}
                      width={20}
                      height={20}
                      sizes="20px"
                      quality={75}
                      style={{ borderRadius: 999 }}
                    />
                    <span className={classes.langChevron} aria-hidden="true">
                      ▾
                    </span>
                  </Group>
                </UnstyledButton>
              </Menu.Target>

              <Menu.Dropdown>
                {(['fr', 'en'] as Locale[]).map((loc) => {
                  const meta = data.supportedLanguage[loc];
                  return (
                    <Menu.Item
                      key={loc}
                      onClick={() => changeLanguage(loc)}
                      leftSection={
                        <img
                          src={meta.img}
                          alt={meta.label}
                          width={18}
                          height={18}
                          style={{ borderRadius: 999 }}
                        />
                      }
                    >
                      {meta.label}
                    </Menu.Item>
                  );
                })}
              </Menu.Dropdown>
            </Menu>

            {/* Auth block */}
            {isLoggedIn ? (
              <>
                <button type="button" onClick={handleLogout} className={classes.rightLinkButton}>
                  {data.auth.logoutLabel}
                </button>
                <Link href={data.auth.dashboardHref} className={classes.rightLink}>
                  {data.auth.dashboardLabel}
                </Link>
              </>
            ) : (
              <Link href={data.auth.loginHref} className={classes.rightLink}>
                {data.auth.loginLabel}
              </Link>
            )}
          </div>

          {data.cta && (
            <Link href={data.cta.href} className={classes.cta}>
              {data.cta.label}
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}
